/**
 * AccountAbbreviationModal.jsx
 *
 * User request (2026-03-01):
 *   "A lot of accounts have very long names… I want an option to pop up
 *    after they're extracted to abbreviate those names and shorten them
 *    up a little bit. For instance, Capital One → Cap One. I want to be
 *    able to manually edit the abbreviations if I don't like them before
 *    we go to the next screen where they are parsed.
 *
 *    Also, if an account is on the credit profile multiple times, like
 *    Affirm 4-5 times, I don't want to list them individually. Group
 *    them: '3× Affirm' with three balances added into one total OR kept
 *    as three separate amounts — I'm not sure how I want to do it. I'm
 *    just trying to pack as many accounts on an FTC report as possible
 *    because we only have five slots."
 *
 * This modal opens after `parseAccounts()` completes.  It receives the
 * freshly parsed accounts and lets the admin:
 *   1. Preview the auto-suggested short forms for every creditor name.
 *   2. Manually override any suggestion inline.
 *   3. Optionally MERGE duplicates (same creditor) into one row, with
 *      either a summed balance or comma-separated separate balances.
 *   4. Continue — the transformed accounts replace the raw parsed list.
 */

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, X, RefreshCw, Package, Users, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/** Parse a date-ish string into a comparable value. Supports MM/DD/YYYY,
 *  MM-DD-YYYY, YYYY-MM-DD, "Aug 2019", "08/2019". Returns Number.MAX_VALUE
 *  when unparseable so those rows lose the "earliest" race. */
function toSortableDate(s) {
  if (!s) return Number.MAX_VALUE;
  const raw = String(s).trim();
  // MM/DD/YYYY or MM-DD-YYYY
  let m = raw.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (m) {
    let [, mm, dd, yy] = m;
    if (yy.length === 2) yy = (parseInt(yy, 10) >= 50 ? '19' : '20') + yy;
    return new Date(`${yy}-${mm.padStart(2,'0')}-${dd.padStart(2,'0')}`).getTime();
  }
  // YYYY-MM-DD
  m = raw.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
  if (m) return new Date(`${m[1]}-${m[2].padStart(2,'0')}-${m[3].padStart(2,'0')}`).getTime();
  // MM/YYYY
  m = raw.match(/^(\d{1,2})[\/\-](\d{4})$/);
  if (m) return new Date(`${m[2]}-${m[1].padStart(2,'0')}-01`).getTime();
  // "Aug 2019" / "August 2019"
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : Number.MAX_VALUE;
}

/** Return the earliest dateOpened string from a group of rows.  Returns
 *  the RAW original string of the earliest row so we preserve its
 *  formatting (MM/DD/YYYY etc.) exactly as the user parsed it. */
function earliestDateOpened(rows) {
  let best = null;
  let bestTs = Infinity;
  for (const r of rows) {
    const s = r.dateOpened || '';
    if (!s) continue;
    const ts = toSortableDate(s);
    if (ts < bestTs) { bestTs = ts; best = s; }
  }
  return best;
}

/** Strip trailing digit-containing tokens from a raw name while
 *  PRESERVING the original casing (so "DoE E1234" → "DoE", not "Doe").
 *  Used for the merged-row display so brand casing (Amex, PRA, DoE,
 *  BofA, 5/3, etc.) survives the grouping step. */
function stripTrailingAccountNum(s) {
  if (!s) return '';
  const words = String(s).trim().split(/\s+/);
  while (words.length > 1 && /\d/.test(words[words.length - 1])) {
    words.pop();
  }
  return words.join(' ').replace(/[\s,\-]+$/, '').trim();
}

/** Group accounts by normalized creditor name. Drops trailing tokens
 *  that contain any digit (account numbers like "1002", "E1234", "4443")
 *  so "Affirm 1002", "Affirm 1003" and "DoE E1234", "DoE E2234" all land
 *  in the same "affirm" / "doe" bucket when merge-duplicates is on. */
function normalizeForGroup(s) {
  return stripTrailingAccountNum(s).toLowerCase();
}

function groupByCreditor(rows) {
  const g = new Map();
  for (const r of rows) {
    const key = normalizeForGroup(r.short || r.creditorName || '');
    if (!key) continue;
    if (!g.has(key)) g.set(key, []);
    g.get(key).push(r);
  }
  return g;
}

/** Sum a set of dollar strings ("$1,234.56", "1234", "$800.00") → number. */
function sumDollars(rows) {
  return rows.reduce((acc, r) => {
    const raw = String(r.fraudAmount ?? r.balance ?? '').replace(/[$,\s]/g, '');
    const n = parseFloat(raw);
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);
}

export default function AccountAbbreviationModal({
  open,
  onOpenChange,
  accounts,        // raw parsedAccounts from FTC Builder
  onApply,         // (transformed rows) => void
}) {
  const [rows, setRows] = useState([]);           // [{id, creditorName, short, fraudAmount, ...}]
  const [busySuggest, setBusySuggest] = useState(false);
  const [mergeDuplicates, setMergeDuplicates] = useState(false);

  // ── Load rows and fetch suggestions when the modal opens ────────
  useEffect(() => {
    if (!open) return;
    // Seed rows with current creditorName as both original + short
    const seeded = (accounts || []).map((a, i) => ({
      ...a,
      _rowId: a.id || `row-${i}`,
      originalName: a.originalName || a.creditorName || '',
      short: a.creditorName || '',
    }));
    setRows(seeded);
    // Fire off the suggest call
    if (!seeded.length) return;
    (async () => {
      setBusySuggest(true);
      try {
        const uniqueNames = Array.from(new Set(seeded.map(r => r.originalName)));
        const { data } = await axios.post(`${API}/api/ftc-report/suggest-abbreviations`, {
          names: uniqueNames,
        });
        const map = new Map((data?.items || []).map(x => [x.original, x.suggested]));
        setRows(prev => prev.map(r => ({
          ...r,
          short: map.get(r.originalName) || r.short || r.originalName,
        })));
      } catch (e) {
        // Non-fatal — user still edits manually
        console.warn('Abbrev suggest failed', e);
      } finally {
        setBusySuggest(false);
      }
    })();
    // Intentionally only re-run when `open` toggles; other deps are
    // stable refs.  (Rule not configured in this project's eslint set.)
  }, [open]);

  // ── Editing helpers ────────────────────────────────────────────
  const updateShort = (rowId, value) => {
    setRows(prev => prev.map(r => r._rowId === rowId ? { ...r, short: value } : r));
  };
  const resetShort = (rowId) => {
    setRows(prev => prev.map(r => r._rowId === rowId ? { ...r, short: r.originalName } : r));
  };
  const resuggestAll = async () => {
    setBusySuggest(true);
    try {
      const uniqueNames = Array.from(new Set(rows.map(r => r.originalName)));
      const { data } = await axios.post(`${API}/api/ftc-report/suggest-abbreviations`, {
        names: uniqueNames,
      });
      const map = new Map((data?.items || []).map(x => [x.original, x.suggested]));
      setRows(prev => prev.map(r => ({ ...r, short: map.get(r.originalName) || r.originalName })));
      toast.success('Re-suggested short forms for all accounts');
    } catch (e) {
      toast.error('Suggest failed — edit manually');
    } finally {
      setBusySuggest(false);
    }
  };

  // ── Duplicate preview ──────────────────────────────────────────
  const duplicateGroups = useMemo(() => {
    const g = groupByCreditor(rows);
    return Array.from(g.entries()).filter(([_, arr]) => arr.length > 1);
  }, [rows]);

  const slotsAfter = useMemo(() => {
    if (!mergeDuplicates) return rows.length;
    const g = groupByCreditor(rows);
    return g.size;
  }, [rows, mergeDuplicates]);

  // ── Apply merge + hand back ────────────────────────────────────
  const handleApply = () => {
    let out;
    if (!mergeDuplicates) {
      out = rows.map(r => {
        const shortText = r.short?.trim() || r.originalName;
        return {
          ...r,
          creditorName: shortText,
          // FTCBuilderPage displays `originalName || creditorName…` so
          // we must overwrite originalName to make the short form
          // actually appear in the editable row above.
          originalName: shortText,
          displayName: shortText,
        };
      });
    } else {
      const groups = groupByCreditor(rows);
      out = [];
      for (const [key, group] of groups.entries()) {
        if (group.length === 1) {
          const r = group[0];
          out.push({
            ...r,
            creditorName: r.short?.trim() || r.originalName,
            displayName: r.short?.trim() || r.originalName,
          });
          continue;
        }
        // Merge: prefix count, SUM balances, use EARLIEST open date.
        // Per user (2026-03-01): "10 - US Department of Education, all
        // 10 accounts totaled to one number, open date is when the first
        // account was opened."  Dropping individual dates is intentional
        // — keeping them makes it too messy on the FTC report.
        const base = group[0];
        // Compute the merged-row display: strip trailing account numbers
        // while preserving the brand casing ("DoE E1234" → "DoE", not "Doe").
        const shortName = stripTrailingAccountNum(base.short?.trim() || base.originalName) || base.originalName;
        const totalNum = sumDollars(group);
        const totalStr = `$${totalNum.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
        const earliest = earliestDateOpened(group) || base.dateOpened || '';
        const mergedName = `${group.length} - ${shortName}`;
        const merged = {
          ...base,
          creditorName: mergedName,
          // FTCBuilderPage renders `acc.originalName || acc.creditorName…`
          // as the editable display, so we MUST overwrite originalName
          // too or the merged row shows the old raw text.
          originalName: mergedName,
          displayName: mergedName,
          fraudAmount: totalStr,
          balance: totalStr,
          dateOpened: earliest,
          _mergedFromIds: group.map(g => g.id).filter(Boolean),
          _mergedCount: group.length,
          _mergedOriginalRows: group.map(g => ({
            creditorName: g.originalName || g.creditorName,
            balance: g.fraudAmount ?? g.balance ?? '',
            dateOpened: g.dateOpened || '',
          })),
        };
        out.push(merged);
      }
    }
    onApply(out);
    onOpenChange(false);
    toast.success(
      mergeDuplicates
        ? `Applied — ${out.length} row(s) (merged ${rows.length - out.length} duplicate${rows.length - out.length === 1 ? '' : 's'})`
        : `Applied ${out.length} short-form name(s)`
    );
  };

  const totalDuplicates = duplicateGroups.reduce((sum, [, arr]) => sum + arr.length, 0);
  const savedSlots = mergeDuplicates ? (rows.length - slotsAfter) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-900 border-zinc-700 text-white max-w-4xl max-h-[90vh] flex flex-col"
        data-testid="account-abbreviation-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-purple-400" />
            Shorten &amp; Merge Accounts <span className="text-[11px] text-zinc-500 italic font-normal">(optional)</span>
          </DialogTitle>
          <p className="text-[12px] text-zinc-400">
            Edit any suggested short name below. <b>Merging duplicate creditors is optional</b> — turn it on only if you want to pack more accounts into the 5-slot FTC report. When merged, a group shows as <span className="text-white font-mono">10 - US Dept of Education</span> with all balances summed into one total and the earliest open date. Individual dates for the other 9 accounts are intentionally dropped so the row stays clean. Click <b>Skip — use original</b> anytime to keep your parsed accounts exactly as they are.
          </p>
        </DialogHeader>

        {/* ── Controls row ────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3 px-1 border-b border-zinc-800 pb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={resuggestAll}
            disabled={busySuggest}
            className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-white text-xs"
            data-testid="abbrev-resuggest-all"
          >
            {busySuggest
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Suggesting…</>
              : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Re-suggest all</>}
          </Button>
          <label className="flex items-center gap-2 text-[12px] text-zinc-300 cursor-pointer">
            <input
              type="checkbox"
              checked={mergeDuplicates}
              onChange={(e) => setMergeDuplicates(e.target.checked)}
              className="w-4 h-4 accent-fuchsia-500"
              data-testid="abbrev-merge-toggle"
            />
            <Users className="w-4 h-4 text-fuchsia-400" />
            Merge duplicate creditors <span className="text-zinc-500 italic">(optional — off by default)</span>
            {totalDuplicates > 1 && (
              <span className="text-zinc-500">— {totalDuplicates} rows across {duplicateGroups.length} creditor{duplicateGroups.length === 1 ? '' : 's'}</span>
            )}
          </label>
          <div className="ml-auto text-[11px] text-zinc-400">
            {mergeDuplicates ? (
              <>rows after apply: <b className="text-white">{slotsAfter}</b>{savedSlots > 0 && <span className="text-emerald-400"> · saves {savedSlots} slot{savedSlots === 1 ? '' : 's'}</span>}</>
            ) : (
              <>rows: <b className="text-white">{rows.length}</b></>
            )}
          </div>
        </div>

        {/* ── Rows list ───────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto pr-1 space-y-2 py-2" data-testid="abbrev-rows">
          {rows.length === 0 && (
            <div className="text-center text-zinc-500 text-sm py-8">
              No accounts to abbreviate yet.
            </div>
          )}
          {rows.map((r, idx) => {
            const isDup = mergeDuplicates && duplicateGroups.some(([k]) => k === normalizeForGroup(r.short || ''));
            return (
              <div
                key={r._rowId}
                className={`grid grid-cols-12 gap-2 items-center rounded-md border p-2 ${
                  isDup
                    ? 'border-fuchsia-500/40 bg-fuchsia-500/5'
                    : 'border-zinc-800 bg-zinc-950/50'
                }`}
                data-testid={`abbrev-row-${idx}`}
              >
                <div className="col-span-5 min-w-0">
                  <div className="text-[10px] uppercase text-zinc-500">Original</div>
                  <div className="text-xs text-zinc-300 truncate" title={r.originalName}>
                    {r.originalName}
                  </div>
                </div>
                <div className="col-span-5">
                  <div className="text-[10px] uppercase text-zinc-500">Short (editable)</div>
                  <Input
                    value={r.short || ''}
                    onChange={(e) => updateShort(r._rowId, e.target.value)}
                    className="h-7 bg-zinc-900 border-zinc-700 text-white text-xs"
                    data-testid={`abbrev-short-input-${idx}`}
                  />
                </div>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  {r.short !== r.originalName && (
                    <button
                      onClick={() => resetShort(r._rowId)}
                      className="text-[10px] text-blue-400 hover:text-blue-300 underline"
                      data-testid={`abbrev-reset-${idx}`}
                    >
                      reset
                    </button>
                  )}
                </div>
                {(r.fraudAmount || r.balance) && (
                  <div className="col-span-12 text-[10px] text-zinc-500">
                    <span className="text-zinc-600">balance:</span>{' '}
                    <span className="text-zinc-400">{r.fraudAmount || r.balance}</span>
                    {r.dateOpened && <span className="text-zinc-600"> · {r.dateOpened}</span>}
                    {r.category && <span className="text-zinc-600"> · {r.category}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Merge preview (only when toggle is on) ──────────── */}
        {mergeDuplicates && duplicateGroups.length > 0 && (
          <div className="border-t border-zinc-800 pt-3 pr-1">
            <div className="text-[11px] uppercase text-fuchsia-300 font-semibold mb-1 flex items-center gap-2">
              <Users className="w-3 h-3" />
              Merge preview — how each group will appear on the FTC report
            </div>
            <div className="max-h-40 overflow-y-auto space-y-2 pl-1">
              {duplicateGroups.map(([k, group]) => {
                const shortName = stripTrailingAccountNum(group[0].short?.trim() || group[0].originalName) || group[0].originalName;
                const totalNum = sumDollars(group);
                const totalStr = `$${totalNum.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
                const earliest = earliestDateOpened(group) || '(no date)';
                return (
                  <div key={k} className="rounded border border-fuchsia-500/30 bg-fuchsia-950/30 p-2 text-[11px] text-zinc-300 space-y-1">
                    <div className="flex items-center gap-2">
                      <Check className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                      <b className="text-white text-[12px]">{group.length} - {shortName}</b>
                      <span className="text-zinc-500">·</span>
                      <span className="text-emerald-300 font-mono">{totalStr}</span>
                      <span className="text-zinc-500">·</span>
                      <span className="text-zinc-400">opened {earliest}</span>
                    </div>
                    <div className="pl-5 text-[10px] text-zinc-500 space-y-0.5">
                      {group.map((g, gi) => (
                        <div key={gi} className="flex items-center gap-2">
                          <span className="text-zinc-600">•</span>
                          <span className="truncate max-w-xs" title={g.originalName}>{g.originalName}</span>
                          <span className="text-zinc-600">·</span>
                          <span className="font-mono">{g.fraudAmount || g.balance || '—'}</span>
                          <span className="text-zinc-700">{g.dateOpened || '—'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter className="border-t border-zinc-800 pt-3 gap-2 flex-col sm:flex-row items-stretch sm:items-center">
          <div className="text-[10px] text-zinc-500 mr-auto italic">
            Everything here is optional. "Skip" leaves your parsed accounts exactly as they are.
          </div>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-white"
            data-testid="abbrev-cancel"
          >
            <X className="w-4 h-4 mr-1" /> Skip — use original
          </Button>
          <Button
            onClick={handleApply}
            disabled={rows.length === 0}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="abbrev-apply"
          >
            <Check className="w-4 h-4 mr-1" />
            {mergeDuplicates
              ? `Apply — ${slotsAfter} row${slotsAfter === 1 ? '' : 's'}`
              : `Apply short names (${rows.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
